/**
 * DeliverableCard — individual deliverable card with title, rating,
 * competency chips, well done/improvement textareas with voice
 */
import { useState } from 'react';
import {
  C, getRatingColor, getRatingLabel, getCompetencies,
  getCompetencyDescription, getTrack, ALL_LEVELS, LEADERSHIP_READINESS, LEADERSHIP_VALUES, getLeadershipValueGroup,
} from '../../data/managerReviewData';
import type { Employee, Deliverable, FeedbackItem } from '../../types/managerReviewV2';
import type { MultiSourceInsight } from './reviewRefine';
import ReviewTextarea from './ReviewTextarea';

// Leadership Value text comes from the shared data module (the full criteria). Chips are named
// "Push FINN to the Next Level" (LEADERSHIP_READINESS's key); the shared map spells it "next Level".
const leadershipValueDesc = (level: string, name: string): string | undefined => {
  const group = LEADERSHIP_VALUES[getLeadershipValueGroup(level)];
  return group?.[name] ?? group?.[name.replace('Next Level', 'next Level')];
};

const renderDesc = (text: string, itemStyle: React.CSSProperties = {}) => {
  if (!text) return null;
  const cleaned = text.replace(/\n\*\*\n/g, '\n\n').replace(/\n\*\*$/, '').replace(/^\*\*\n/, '');
  const sentences = cleaned.includes('\n\n')
    ? cleaned.split('\n\n').map(s => s.trim()).filter(Boolean)
    : cleaned.includes('\n')
    ? cleaned.split('\n').map(s => s.trim()).filter(Boolean)
    : cleaned.split(/\. (?=[A-Z])/).map((s, i, a) => i < a.length - 1 ? s + '.' : s).filter(Boolean);
  return (
    <ul style={{ paddingLeft: 16, margin: 0, listStyleType: 'disc' }}>
      {sentences.map((s, i) => (
        <li key={i} style={{ fontSize: 12, lineHeight: 1.6, color: '#374151', marginBottom: 3, ...itemStyle }}>
          {renderBold(s)}
        </li>
      ))}
    </ul>
  );
};

const renderBold = (text: string): React.ReactNode => {
  if (!text || !text.includes('**')) return text;
  const parts = text.split('**');
  return parts.map((p, i) => i % 2 === 1 ? <strong key={i}>{p}</strong> : p);
};

interface DeliverableCardProps {
  index: number;
  employee: Employee;
  level: string;
  data: Deliverable;
  /** Takes an updater, so async AI callbacks apply to the latest data, not a stale render snapshot. */
  onChange: (update: (prev: Deliverable) => Deliverable) => void;
  onRemove: () => void;
  selfReview: string;
  feedbackItems: FeedbackItem[];
  perfFeedbackRaw?: string;
  anytimeFeedbackRaw?: string;
  id?: string;
}

export default function DeliverableCard({ index, employee, level, data, onChange, onRemove, selfReview, feedbackItems, perfFeedbackRaw, anytimeFeedbackRaw, id }: DeliverableCardProps) {
  const isM = getTrack(level) === 'M';
  const LV_NAMES = ['Deliver Exceptional Results', 'Fuel Talent', 'Push FINN to the Next Level'];
  const LR_NAMES = ['Deliver Exceptional Results', 'Fuel Talent', 'Push FINN to the Next Level'];
  const roleComps = getCompetencies(employee.role, level);
  const roleNotFound = roleComps === null;
  const competencies = roleNotFound
    ? []
    : isM
      ? [...roleComps, ...LV_NAMES]
      : employee.hasLeadershipReadiness
        ? [...roleComps, ...LR_NAMES]
        : roleComps;
  // Competencies saved in the draft that are no longer in the current role's list (role change)
  const staleSelected = (data.competencies || []).filter(c => !competencies.includes(c));

  const [improvProcessSignal, setImprovProcessSignal] = useState(0);
  const [wellInsights, setWellInsights] = useState<MultiSourceInsight[]>([]);
  const [improvInsights, setImprovInsights] = useState<MultiSourceInsight[]>([]);
  const allInsights = [...wellInsights, ...improvInsights];
  const sortOrder: Record<string, number> = { difference: 0, gap: 1, alignment: 2 };
  const sortedInsights = [...allInsights].sort(function(a, b) { return (sortOrder[a.type] || 0) - (sortOrder[b.type] || 0); });
  const hasInsights = allInsights.length > 0;
  const hasBothSections = !!(data.wellDone && data.wellDone.trim()) && !!(data.improvement && data.improvement.trim());
  const [multiSourceOpen, setMultiSourceOpen] = useState(true);
  const INSIGHT_STYLES: Record<string, { border: string; bg: string; label: string; labelBg: string; labelColor: string; textColor: string }> = {
    alignment: { border: '#97C459', bg: '#EAF3DE', label: 'Alignment', labelBg: '#C0DD97', labelColor: '#3B6D11', textColor: '#27500A' },
    gap: { border: '#EF9F27', bg: '#FAEEDA', label: 'Gap', labelBg: '#FAC775', labelColor: '#854F0B', textColor: '#633806' },
    difference: { border: '#F09595', bg: '#FCEBEB', label: 'Difference', labelBg: '#F7C1C1', labelColor: '#A32D2D', textColor: '#791F1F' },
  };
  const [showNextComp, setShowNextComp] = useState(false);
  const [ic4DelivTrack, setIc4DelivTrack] = useState<'E' | 'M'>('E');
  const isIC4 = level === 'IC4';

  const levelIdx = ALL_LEVELS.indexOf(level as typeof ALL_LEVELS[number]);
  const nextLevel = isIC4
    ? (ic4DelivTrack === 'E' ? 'E5' : 'M5')
    // E8 is the top of the Expert track: the next entry in ALL_LEVELS (M5) is not a promotion.
    : (levelIdx >= 0 && level !== 'E8' && levelIdx < ALL_LEVELS.length - 1 ? ALL_LEVELS[levelIdx + 1] : null);
  const nextIsM = nextLevel ? getTrack(nextLevel) === 'M' : false;
  const nextLV = isIC4 ? (ic4DelivTrack === 'E' ? 'E5' : 'M5') : nextLevel;
  const nextRoleComps = nextLevel ? (getCompetencies(employee.role, nextLevel) || []) : [];
  const nextCompetencies = nextLevel ? (nextIsM ? [...nextRoleComps, ...LV_NAMES] : nextRoleComps) : [];


  const accentColor = data.rating ? getRatingColor(data.rating) : C.border;

  return (
    <div id={id} style={{
      background: C.card, borderRadius: 10, padding: 24,
      boxShadow: '0 2px 10px rgba(0,0,0,0.07)', border: `1px solid ${C.border}`,
      marginBottom: 16, borderLeft: `3px solid ${accentColor}`,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{
            width: 28, height: 28, borderRadius: 7, background: '#1a1a1a', color: '#fff',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 13, fontWeight: 800, flexShrink: 0,
          }}>{index + 1}</div>
          <div style={{ fontWeight: 700, fontSize: 15, color: C.text }}>Deliverable {index + 1}</div>
        </div>
        {index > 0 && (
          <button
            onClick={onRemove}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.muted, fontSize: 20, lineHeight: 1, padding: '0 4px' }}
          >×</button>
        )}
      </div>

      {/* Title */}
      <div style={{ marginBottom: 14 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: '#555', marginBottom: 6 }}>
          Deliverable title / OKR
        </div>
        <input
          type="text"
          value={data.title || ''}
          onChange={e => { const title = e.target.value; onChange(prev => ({ ...prev, title })); }}
          placeholder="e.g. Launch pricing model for B2B segment (Q1 OKR)"
          style={{
            width: '100%', padding: '10px 12px', border: `1px solid ${C.border}`,
            borderRadius: 6, background: C.inputBg, fontSize: 14, outline: 'none',
            color: C.text, boxSizing: 'border-box',
          }}
        />
      </div>

      {/* Competency chips */}
      <div style={{ marginBottom: 14 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: '#555', marginBottom: 2 }}>
          Competencies Demonstrated
        </div>
        {roleNotFound ? (
          <div style={{
            marginTop: 6, padding: '10px 14px',
            background: 'rgba(232,41,74,0.06)', border: '1px solid rgba(232,41,74,0.35)',
            borderRadius: 7, fontSize: 12, color: '#b91c2e', lineHeight: 1.5,
          }}>
            {/* Name, never an email address: this bundle is publicly downloadable. */}
            ⚠️ Role <strong>"{employee.role}"</strong> is not in the competency database — competency chips can't be shown.
            Please contact <strong>Anna Kaiser</strong> (People team) to get this role added.
          </div>
        ) : (
        <>
        <div style={{ fontSize: 11, color: C.muted, marginBottom: 6 }}>
          Click to tag
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 4 }}>
          {competencies.map(c => {
            const active = (data.competencies || []).includes(c);
            return (
              <button
                key={c}
                onClick={() => {
                  onChange(prev => {
                    const curr = prev.competencies || [];
                    return { ...prev, competencies: curr.includes(c) ? curr.filter(x => x !== c) : [...curr, c] };
                  });
                }}
                style={{
                  padding: '4px 12px', borderRadius: 20,
                  border: `1.5px solid ${active ? C.blue : C.border}`,
                  background: active ? C.blue : '#fff',
                  color: active ? '#fff' : C.text,
                  fontSize: 12, cursor: 'pointer', fontWeight: active ? 600 : 400,
                  fontFamily: 'Inter', transition: 'all 0.1s',
                }}
              >{c}</button>
            );
          })}
        </div>

        {staleSelected.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8, alignItems: 'center' }}>
            <span style={{ fontSize: 10, color: '#bbb', fontStyle: 'italic', whiteSpace: 'nowrap' }}>Previous role:</span>
            {staleSelected.map(c => (
              <button
                key={c}
                onClick={() => onChange(prev => ({ ...prev, competencies: (prev.competencies || []).filter(x => x !== c) }))}
                title={`"${c}" was tagged under a previous role. The competency list has since changed — reach out to Anna Kaiser if you have questions.`}
                style={{
                  padding: '4px 12px', borderRadius: 20,
                  border: '1.5px dashed #d1d5db',
                  background: '#fafafa', color: '#aaa',
                  fontSize: 12, cursor: 'pointer', fontWeight: 400,
                  fontFamily: 'Inter', transition: 'all 0.1s',
                }}
              >{c} ⚠</button>
            ))}
          </div>
        )}

        {(data.competencies || []).length > 0 && (
          <>
            {nextLevel && (
              <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6, marginTop: 10 }}>
                {isIC4 && (
                  <>
                    <span style={{ fontSize: 11, color: C.muted }}>next track:</span>
                    {(['E', 'M'] as const).map((t) => (
                      <button key={t} onClick={() => setIc4DelivTrack(t)} style={{
                        padding: '2px 8px', borderRadius: 12,
                        border: `1.5px solid ${ic4DelivTrack === t ? C.blue : C.border}`,
                        background: ic4DelivTrack === t ? C.lightBlue : '#fff',
                        color: ic4DelivTrack === t ? C.blue : C.muted,
                        fontSize: 11, fontWeight: ic4DelivTrack === t ? 700 : 400,
                        cursor: 'pointer', fontFamily: 'Inter',
                      }}>
                        {t === 'E' ? 'Expert (E5)' : 'Leadership (M5)'}
                      </button>
                    ))}
                  </>
                )}
                <button
                  onClick={() => setShowNextComp(n => !n)}
                  style={{
                    fontSize: 11, color: C.blue, background: 'none',
                    border: `1px solid ${C.blue}50`, borderRadius: 4,
                    padding: '3px 8px', cursor: 'pointer', fontFamily: 'Inter',
                  }}
                >
                  {showNextComp ? '▲ Hide' : '▼ Show'} {nextLV} expectations
                </button>
              </div>
            )}

            <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 10 }}>
              {(data.competencies || []).map(c => {
                const desc = (LV_NAMES.includes(c) && isM)
                  ? leadershipValueDesc(level, c)
                  : (LR_NAMES.includes(c) && employee.hasLeadershipReadiness)
                    ? LEADERSHIP_READINESS[c]
                    : getCompetencyDescription(c, level, employee.role);
                // Role competencies keep their own next-level text on the M track too.
                const nextDesc = showNextComp && nextLevel
                  ? ((nextIsM && LV_NAMES.includes(c))
                    ? leadershipValueDesc(nextLevel, c)
                    : getCompetencyDescription(c, nextLevel, employee.role))
                  : null;
                return (
                  <div key={c}>
                    {desc && (
                      <div style={{
                        padding: '8px 12px', background: C.blue + '0d',
                        borderRadius: nextDesc ? '6px 6px 0 0' : 6,
                        borderLeft: `3px solid ${C.blue}40`,
                      }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: C.blue, marginBottom: 5 }}>{c} at {level}</div>
                        {renderDesc(desc)}
                      </div>
                    )}
                    {nextDesc && (
                      <div style={{
                        padding: '8px 12px', background: C.blue + '08',
                        borderRadius: desc ? '0 0 6px 6px' : 6,
                        borderLeft: `3px solid ${C.blue}25`,
                        borderTop: desc ? `1px dashed ${C.blue}30` : undefined,
                      }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: C.blue, marginBottom: 5 }}>{c} at {nextLV}</div>
                        {renderDesc(nextDesc, { color: '#4b6fa8' })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}
        </>
        )}
      </div>

      {/* Rating */}
      <div style={{ marginBottom: 16, paddingTop: 14, borderTop: '1px solid #ebebeb' }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: '#555', marginBottom: 8 }}>
          Rating
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ display: 'flex', gap: 5 }}>
            {[1, 2, 3, 4, 5].map(r => {
              const isSelected = data.rating === r;
              const col = getRatingColor(r);
              return (
                <button
                  key={r}
                  onClick={() => onChange(prev => ({ ...prev, rating: prev.rating === r ? null : r }))}
                  style={{
                    width: 34, height: 34, borderRadius: 6,
                    border: `1.5px solid ${isSelected ? col : '#d1d5db'}`,
                    background: isSelected ? col : '#fff',
                    color: isSelected ? '#fff' : '#111',
                    fontSize: 13, fontWeight: 700, cursor: 'pointer',
                    fontFamily: 'Inter', transition: 'all 0.1s',
                  }}
                >{r}</button>
              );
            })}
          </div>
          <span style={{ fontSize: 12, color: data.rating ? getRatingColor(data.rating) : '#999', fontWeight: data.rating ? 600 : 400 }}>
            {data.rating ? getRatingLabel(data.rating) : 'Select a rating'}
          </span>
        </div>
      </div>

      {/* Well done / Improvement textareas */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#555', marginBottom: 6 }}>
            What went well?
          </div>
          <ReviewTextarea
            value={data.wellDone || ''}
            onChange={v => onChange(prev => ({ ...prev, wellDone: v }))}
            placeholder="What did they do particularly well? Be specific, include impact and examples. Type or hit Record to speak your thoughts."
            voice
            aiContext={{ employeeName: employee.name, role: employee.role, level, deliverable: data.title, taggedCompetencies: data.competencies, availableCompetencies: competencies, field: 'strengths', selfReview, feedbackItems, perfFeedbackRaw, anytimeFeedbackRaw }}
            onRedirected={text => { onChange(prev => ({ ...prev, improvement: (prev.improvement ? prev.improvement + '\n\n' : '') + text })); setImprovProcessSignal(n => n + 1); }}
            onMultiSource={setWellInsights}
          />
        </div>
        <div>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#555', marginBottom: 6 }}>
            What could have gone better?
          </div>
          <ReviewTextarea
            value={data.improvement || ''}
            onChange={v => onChange(prev => ({ ...prev, improvement: v }))}
            placeholder="Where could they have delivered more or approached things differently. Type or hit Record to speak your thoughts."
            voice
            aiContext={{ employeeName: employee.name, role: employee.role, level, deliverable: data.title, taggedCompetencies: data.competencies, availableCompetencies: competencies, field: 'improvements', selfReview, feedbackItems, perfFeedbackRaw, anytimeFeedbackRaw }}
            processSignal={improvProcessSignal}
            onMultiSource={setImprovInsights}
          />
        </div>
      </div>

      {hasBothSections && hasInsights && (
        <div style={{ marginTop: 12, borderRadius: 8, border: '0.5px solid ' + C.border, background: '#fafafa', fontFamily: 'Inter', overflow: 'hidden' }}>
          <div
            onClick={function() { setMultiSourceOpen(function(o) { return !o; }); }}
            style={{ padding: '10px 14px', display: 'flex', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }}
          >
            <span style={{ fontSize: 12, fontWeight: 700, color: '#555', display: 'flex', alignItems: 'center', gap: 5 }}>
              <span style={{ display: 'inline-block', transition: 'transform 0.15s', transform: multiSourceOpen ? 'rotate(90deg)' : 'rotate(0deg)', fontSize: 9 }}>▶</span>
              Multi-source alignment
            </span>
          </div>
          {multiSourceOpen && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '0 14px 12px' }}>
              {sortedInsights.map(function(insight, i) {
                const s = INSIGHT_STYLES[insight.type];
                return (
                  <div key={i} style={{ borderRadius: 8, border: '0.5px solid ' + s.border, background: s.bg, padding: '8px 12px' }}>
                    <span style={{ display: 'inline-block', fontSize: 10, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.3px', color: s.labelColor, background: s.labelBg, padding: '2px 8px', borderRadius: 4, marginBottom: 4 }}>{s.label}</span>
                    <div style={{ fontSize: 12, lineHeight: 1.6, color: s.textColor }}>{insight.message}</div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Suppress unused variable warning */}
      {nextCompetencies.length === 0 && null}
    </div>
  );
}
